import { useState, useRef } from "react";
import { TRACE_PATH } from "@/data/exercises";
import { MapPin, Flag } from "lucide-react";

export default function PathTrace({ onComplete }) {
  const areaRef = useRef(null);
  const [reached, setReached] = useState(0); // next index to reach
  const [tracing, setTracing] = useState(false);
  const stray = useRef(0);
  const start = useRef(Date.now());
  const done = useRef(false);

  const pointFromEvent = (e) => {
    const rect = areaRef.current.getBoundingClientRect();
    const cx = e.clientX ?? (e.touches && e.touches[0]?.clientX);
    const cy = e.clientY ?? (e.touches && e.touches[0]?.clientY);
    return { x: ((cx - rect.left) / rect.width) * 100, y: ((cy - rect.top) / rect.height) * 100 };
  };

  const finish = () => {
    if (done.current) return;
    done.current = true;
    const duration = Math.round((Date.now() - start.current) / 1000);
    const accuracy = Math.max(50, 100 - stray.current * 3);
    setTimeout(() => onComplete({ score: accuracy, accuracy, duration }), 500);
  };

  const handleMove = (e) => {
    if (!tracing || done.current) return;
    const p = pointFromEvent(e);
    const target = TRACE_PATH[reached];
    if (!target) return;
    const d = Math.hypot(p.x - target.x, p.y - target.y);
    if (d < 11) {
      const next = reached + 1;
      setReached(next);
      if (next >= TRACE_PATH.length) finish();
    } else if (d > 22) {
      stray.current += 1;
    }
  };

  const polyline = TRACE_PATH.map((p) => `${p.x},${p.y}`).join(" ");

  return (
    <div className="w-full max-w-3xl mx-auto text-center" data-testid="pathtrace-game">
      <p className="text-2xl font-patient font-bold text-gray-700 mb-6">
        Posez le doigt sur le point vert et suivez le chemin jusqu'au drapeau
      </p>
      <div
        ref={areaRef}
        data-testid="pathtrace-area"
        onPointerDown={(e) => {
          setTracing(true);
          handleMove(e);
        }}
        onPointerMove={handleMove}
        onPointerUp={() => setTracing(false)}
        onPointerLeave={() => setTracing(false)}
        className="relative w-full h-[24rem] rounded-3xl bg-white border-4 border-emerald-700/20 overflow-hidden touch-none"
        style={{ touchAction: "none" }}
      >
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
          <polyline
            points={polyline}
            fill="none"
            stroke="#F8E4C7"
            strokeWidth="6"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            style={{ strokeWidth: 18 }}
          />
        </svg>
        {TRACE_PATH.map((p, i) => {
          const isStart = i === 0;
          const isEnd = i === TRACE_PATH.length - 1;
          const active = i < reached;
          return (
            <div
              key={i}
              className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-full flex items-center justify-center border-4 border-white transition-colors duration-200 ${
                active ? "bg-emerald-600" : isStart ? "bg-emerald-500 soft-pulse" : isEnd ? "bg-amber-400" : "bg-gray-300"
              }`}
              style={{ top: `${p.y}%`, left: `${p.x}%`, width: 44, height: 44 }}
            >
              {isStart && <MapPin className="w-6 h-6 text-white" />}
              {isEnd && <Flag className="w-6 h-6 text-white" />}
            </div>
          );
        })}
      </div>
      <p className="mt-4 text-lg font-patient text-gray-500">
        Points atteints : {reached}/{TRACE_PATH.length}
      </p>
    </div>
  );
}
